import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SessionContext } from '../../../js/app/contexts/SessionContext';
import {
  orgProviderProfilePath,
  referrerState,
  staffProviderDetailPath,
} from '../utils/navigation';
import { hasPermission, LIST_PERMISSIONS } from '../utils/permissions';
import { TooltippedText } from './tooltipped-text';

type RecordList = {
  key: string;
  label: string;
  icon: string;
  staffPath: string;
  orgPath: string;
  /** The list's `list__` code — the same one the staff and org menus gate that list on. */
  permission: string;
};

/** Order + icons mirror the nav. */
const RECORD_LISTS: RecordList[] = [
  {
    key: 'clearances',
    label: 'View Clearances',
    icon: 'fa-check-double',
    staffPath: '/staff/clearance',
    orgPath: '/app/organization/clearance',
    permission: LIST_PERMISSIONS.clearances,
  },
  {
    key: 'connections',
    label: 'View Connections',
    icon: 'fa-handshake',
    staffPath: '/staff/connections',
    orgPath: '/app/organization/connections',
    permission: LIST_PERMISSIONS.connections,
  },
  {
    key: 'assignments',
    label: 'View Assignments',
    icon: 'fa-calendar-lines-pen',
    staffPath: '/staff/assignments',
    orgPath: '/app/organization/assignments',
    permission: LIST_PERMISSIONS.assignments,
  },
  {
    key: 'timecards',
    label: 'View Timecards',
    icon: 'fa-clock',
    staffPath: '/staff/timecards',
    orgPath: '/app/organization/timecards',
    permission: LIST_PERMISSIONS.timecards,
  },
];

const listHref = (path: string, search: string) =>
  `${path}?queue=all&search=${encodeURIComponent(search)}`;

/**
 * Router links dressed as the shared Button's `small` + `brand-green-outline`
 * variant (real anchors keep middle-click / open-in-new-tab; the Catalyst
 * `Button href` renders a plain `<a>` that full-reloads, so it isn't used).
 */
const OUTLINE_BUTTON_LINK =
  'tw:inline-flex tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-brand-green-0 tw:bg-white tw:shadow-sm ' +
  'tw:px-[calc(theme(spacing[2.5])-1px)] tw:py-[calc(theme(spacing[1])-1px)] tw:sm:px-[calc(theme(spacing[2])-1px)] tw:sm:py-[2px] ' +
  'tw:text-xs tw:!text-brand-green-0 tw:!no-underline tw:hover:bg-brand-green-0 tw:hover:!text-white ' +
  'tw:focus:outline-none tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-brand-green-0';

const IconLink: React.FC<{
  to: string;
  label: string;
  icon: string;
  state: object;
  testId?: string;
}> = ({ to, label, icon, state, testId }) => (
  <TooltippedText tooltipContent={label}>
    <Link
      to={to}
      state={state}
      aria-label={label}
      className={OUTLINE_BUTTON_LINK}
      data-testid={testId}
    >
      <i className={`far fa-fw ${icon}`} />
    </Link>
  </TooltippedText>
);

type ProviderRecordLinksProps = {
  providerId: string;
  /**
   * The provider name as THIS viewer sees it (the viewer-masked display name; an
   * assignment/timecard drawer's full name is the same once revealed). Every list
   * searches the same viewer-masked `provider_display_name`, so one term fits all.
   */
  searchName?: string | null;
  /** Staff lists + full page vs the org lists + legacy org profile. */
  isStaff: boolean;
  /** Names this page on the destination's back link ("← <label>"). */
  referrerLabel: string;
  /** Leading stethoscope link to the provider profile. Off inside the provider drawer itself. */
  showProfileLink?: boolean;
};

/**
 * Icon-only outline buttons for a drawer header; the label lives in the tooltip + aria-label.
 * Every button is scoped to the PROVIDER, not the record the drawer shows: e.g. Connections
 * from a clearance lists all of that provider's connections. Each link carries this page's
 * URL + `referrerLabel` so the destination shows "← <label>" back here.
 */
export const ProviderRecordLinks: React.FC<ProviderRecordLinksProps> = ({
  providerId,
  searchName,
  isStaff,
  referrerLabel,
  showProfileLink = false,
}) => {
  const location = useLocation();
  const { user } = useContext(SessionContext);
  const state = referrerState(location, referrerLabel);
  // Only lists this user can open (mirrors the menus); the staff full page needs the
  // providers list code, the legacy org profile has never been gated.
  const lists = RECORD_LISTS.filter((list) => hasPermission(user, list.permission));
  const canOpenProfile = !isStaff || hasPermission(user, LIST_PERMISSIONS.providers);
  return (
    <div className="tw:flex tw:items-center tw:gap-1.5" data-testid="provider-record-links">
      {showProfileLink && canOpenProfile && (
        <IconLink
          to={isStaff ? staffProviderDetailPath(providerId) : orgProviderProfilePath(providerId)}
          label="View Provider Profile"
          icon="fa-stethoscope"
          state={state}
          testId="provider-profile-link"
        />
      )}
      {searchName &&
        lists.map((list) => (
          <IconLink
            key={list.key}
            to={listHref(isStaff ? list.staffPath : list.orgPath, searchName)}
            label={list.label}
            icon={list.icon}
            state={state}
          />
        ))}
    </div>
  );
};
